import { useEffect, useRef, useState, useCallback } from "react";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, RefreshCw, ZoomIn, ZoomOut, Maximize2, AlertTriangle, GitFork } from "lucide-react";
import API from "../lib/api";
import { useAudit } from "../context/AuditContext";

// ── Node Color Palette (by type) ──────────────────────────────────────────────
const NODE_COLORS = {
  Invoice:      { fill: "#ef4444", stroke: "#b91c1c", text: "#fff" },
  PurchaseOrder:{ fill: "#f97316", stroke: "#c2410c", text: "#fff" },
  Vendor:       { fill: "#8b5cf6", stroke: "#6d28d9", text: "#fff" },
  Employee:     { fill: "#06b6d4", stroke: "#0e7490", text: "#fff" },
  Table:        { fill: "#3b82f6", stroke: "#1d4ed8", text: "#fff" },
  Column:       { fill: "#6366f1", stroke: "#4338ca", text: "#fff" },
  Entity:       { fill: "#64748b", stroke: "#475569", text: "#fff" },
};

function getHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash);
}

function getNodeColor(type) {
  if (NODE_COLORS[type]) {
    return NODE_COLORS[type];
  }
  
  // Stable HSL generator for dynamic entity types from artifacts
  const hash = getHash(type || "Entity");
  const hue = hash % 360;
  const saturation = 65 + (hash % 10);
  const lightness = 45 + (hash % 10);
  
  const fill = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  const stroke = `hsl(${hue}, ${saturation}%, ${lightness - 12}%)`;
  
  return { fill, stroke, text: "#fff" };
}

/**
 * Extracts a human-friendly label from node properties.
 * Prioritizes business identifiers (Invoice #, Name) over technical IDs.
 */
function getNodeLabel(node) {
  if (node.label && node.label !== node.id && !String(node.label).includes(":ARTIFACT:")) {
    return String(node.label);
  }
  
  const p = node.properties || {};
  const candidates = [
    p.invoice_number, p.po_number, p.vendor_name, p.employee_name,
    p.name, p.title, p.table_name, p.column_name, p.file_name
  ];
  const found = candidates.find(c => c && String(c).trim().length > 0);
  if (found) return String(found);
  
  // Clean Fallback: Just the Type (No technical ID)
  return node.type || "Entity";
}

// ── Force-directed layout (simple Verlet integration) ────────────────────────
function initPhysics(nodes, edges) {
  const positions = {};
  const velocities = {};
  const angle = (2 * Math.PI) / Math.max(nodes.length, 1);
  const radius = Math.min(300, nodes.length * 25);

  nodes.forEach((n, i) => {
    positions[n.id] = {
      x: 400 + radius * Math.cos(i * angle),
      y: 300 + radius * Math.sin(i * angle),
    };
    velocities[n.id] = { x: 0, y: 0 };
  });
  return { positions, velocities };
}

function tickPhysics(nodes, edges, positions, velocities) {
  const k = 0.8;
  const repulsion = 4500;
  const attraction = 0.035;
  const damping = 0.75;
  const centerForce = 0.004;
  const cx = 400, cy = 300;

  // Repulsion between all pairs
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const a = nodes[i], b = nodes[j];
      const dx = positions[b.id].x - positions[a.id].x;
      const dy = positions[b.id].y - positions[a.id].y;
      const dist = Math.max(Math.sqrt(dx * dx + dy * dy), 20);
      const force = repulsion / (dist * dist);
      const fx = (dx / dist) * force;
      const fy = (dy / dist) * force;
      velocities[a.id].x -= fx;
      velocities[a.id].y -= fy;
      velocities[b.id].x += fx;
      velocities[b.id].y += fy;
    }
  }

  // Attraction along edges
  edges.forEach(e => {
    const a = positions[e.source];
    const b = positions[e.target];
    if (!a || !b) return;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const force = attraction * (dist - 120);
    const fx = (dx / dist) * force;
    const fy = (dy / dist) * force;
    velocities[e.source].x += fx;
    velocities[e.source].y += fy;
    velocities[e.target].x -= fx;
    velocities[e.target].y -= fy;
  });

  // Weak center gravity
  nodes.forEach(n => {
    velocities[n.id].x += (cx - positions[n.id].x) * centerForce;
    velocities[n.id].y += (cy - positions[n.id].y) * centerForce;
  });

  // Integrate
  nodes.forEach(n => {
    velocities[n.id].x *= damping;
    velocities[n.id].y *= damping;
    positions[n.id].x += velocities[n.id].x;
    positions[n.id].y += velocities[n.id].y;
  });
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function EvidenceGraphPanel({ auditRunId, view = "all", className = "" }) {
  const { graphCache, setGraphCache } = useAudit() || {};
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const physicsRef = useRef(null);
  const graphRef = useRef({ nodes: [], edges: [] });
  const transformRef = useRef({ scale: 1, tx: 0, ty: 0 });
  const dragRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [graphMeta, setGraphMeta] = useState({ nodes: 0, edges: 0, available: null });
  const [selectedNode, setSelectedNode] = useState(null);
  const [activeView, setActiveView] = useState(view);
  const [visStyle, setVisStyle] = useState("standard"); // "standard" or "bloom"

  const graphCacheRef = useRef(null);
  useEffect(() => {
    graphCacheRef.current = graphCache;
  }, [graphCache]);

  // ── Fetch Graph Data ─────────────────────────────────────────────────────
  const fetchGraph = useCallback(async (forceRefresh = false) => {
    if (!auditRunId) return;
    setLoading(true);
    setError(null);
    setSelectedNode(null);
    try {
      const cacheKey = `${auditRunId}-${activeView}`;
      const cacheVal = graphCacheRef.current;
      if (!forceRefresh && cacheVal && cacheVal[cacheKey]) {
        const cachedData = cacheVal[cacheKey];
        if (cachedData.available === false) {
          setError(cachedData.error || "Graph is not available for this environment.");
          setGraphMeta({ nodes: 0, edges: 0, available: false });
          graphRef.current = { nodes: [], edges: [] };
          setLoading(false);
          return;
        }

        const enrichedNodes = (cachedData.nodes || []).map(n => ({
          ...n,
          computedLabel: getNodeLabel(n)
        }));

        graphRef.current = { nodes: enrichedNodes, edges: cachedData.edges || [] };
        setGraphMeta({ nodes: enrichedNodes.length, edges: cachedData.edges?.length || 0, available: true });

        // Init physics
        const { positions, velocities } = initPhysics(enrichedNodes, cachedData.edges || []);
        physicsRef.current = { positions, velocities, ticks: 0 };
        setLoading(false);
        return;
      }

      const { data } = await API.get(
        `/audit-runs/${auditRunId}/graph/evidence`,
        { params: { view: activeView, refresh: forceRefresh } }
      );

      if (!data.available) {
        const errorData = { available: false, error: data.error || "Graph is not available for this environment." };
        if (setGraphCache) {
          setGraphCache(prev => ({ ...prev, [cacheKey]: errorData }));
        }
        setError(errorData.error);
        setGraphMeta({ nodes: 0, edges: 0, available: false });
        graphRef.current = { nodes: [], edges: [] };
        return;
      }

      const enrichedNodes = (data.nodes || []).map(n => ({
        ...n,
        computedLabel: getNodeLabel(n)
      }));

      graphRef.current = { nodes: enrichedNodes, edges: data.edges || [] };
      setGraphMeta({ nodes: enrichedNodes.length, edges: data.edges?.length || 0, available: true });

      if (setGraphCache) {
        setGraphCache(prev => ({ ...prev, [cacheKey]: data }));
      }

      // Init physics
      const { positions, velocities } = initPhysics(enrichedNodes, data.edges || []);
      physicsRef.current = { positions, velocities, ticks: 0 };
    } catch (e) {
      console.error("[EvidenceGraph] Fetch failed:", e);
      setError(e?.response?.data?.detail || e.message || "Failed to load evidence graph.");
    } finally {
      setLoading(false);
    }
  }, [auditRunId, activeView, setGraphCache]);

  useEffect(() => {
    fetchGraph(false);
    // eslint-disable-next-line
  }, [auditRunId, activeView]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handleResize = () => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      
      // Center based on real container size
      if (transformRef.current.tx === 0) {
        transformRef.current.tx = rect.width / 2 - 400; 
        transformRef.current.ty = rect.height / 2 - 300;
      }
    };
    const observer = new ResizeObserver(handleResize);
    observer.observe(canvas.parentElement);
    handleResize();
    return () => observer.disconnect();
  }, []);

  // ── Canvas Renderer ──────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    function draw() {
      const { nodes, edges } = graphRef.current;
      const physics = physicsRef.current;
      const { scale, tx, ty } = transformRef.current;

      // Physics tick (run until stable)
      if (physics && physics.ticks < 300) {
        tickPhysics(nodes, edges, physics.positions, physics.velocities);
        physics.ticks++;
      }

      const dpr = window.devicePixelRatio || 1;

      // Clear the physical canvas bounds
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.restore();

      ctx.save();
      // Apply Device Pixel Ratio scaling for high-DPI crispness
      ctx.scale(dpr, dpr);
      
      // Grid background
      ctx.save();
      ctx.strokeStyle = "rgba(100,116,139,0.07)";
      ctx.lineWidth = 1;
      const gridSize = 40 * scale;
      const rect = canvas.getBoundingClientRect();
      for (let x = (tx % gridSize); x < rect.width; x += gridSize) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, rect.height); ctx.stroke();
      }
      for (let y = (ty % gridSize); y < rect.height; y += gridSize) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(rect.width, y); ctx.stroke();
      }
      ctx.restore();

      // Apply drag panning and zoom scaling
      ctx.translate(tx, ty);
      ctx.scale(scale, scale);
      
      // Configure global smooth line rendering styles
      ctx.lineCap = "round";
      ctx.lineJoin = "round";

      if (!physics || nodes.length === 0) {
        ctx.restore();
        animRef.current = requestAnimationFrame(draw);
        return;
      }

      // Draw edges
      edges.forEach(e => {
        const a = physics.positions[e.source];
        const b = physics.positions[e.target];
        if (!a || !b) return;

        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.strokeStyle = "rgba(148,163,184,0.5)";
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Edge label
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2;
        ctx.font = "9px 'Inter', sans-serif";
        ctx.fillStyle = "rgba(100,116,139,0.85)";
        ctx.textAlign = "center";
        ctx.fillText(e.label || "", mx, my - 4);

        // Arrow
        const angle = Math.atan2(b.y - a.y, b.x - a.x);
        const arrowLen = 8;
        const arrowX = b.x - Math.cos(angle) * 18;
        const arrowY = b.y - Math.sin(angle) * 18;
        ctx.beginPath();
        ctx.moveTo(arrowX, arrowY);
        ctx.lineTo(arrowX - arrowLen * Math.cos(angle - 0.4), arrowY - arrowLen * Math.sin(angle - 0.4));
        ctx.lineTo(arrowX - arrowLen * Math.cos(angle + 0.4), arrowY - arrowLen * Math.sin(angle + 0.4));
        ctx.closePath();
        ctx.fillStyle = "rgba(148,163,184,0.6)";
        ctx.fill();
      });

      // Draw nodes
      nodes.forEach(n => {
        const pos = physics.positions[n.id];
        if (!pos) return;
        const colors = getNodeColor(n.type);
        const isBloom = visStyle === "bloom";
        const r = isBloom 
          ? (n.group === "schema" ? 16 : 22) 
          : (n.group === "schema" ? 12 : 18);

        // Glow for selected or bloom node ambient glow
        if (selectedNode?.id === n.id) {
          ctx.shadowColor = colors.fill;
          ctx.shadowBlur = 20;
        } else if (isBloom) {
          ctx.shadowColor = colors.fill;
          ctx.shadowBlur = 8;
        }

        ctx.beginPath();
        ctx.arc(pos.x, pos.y, r, 0, Math.PI * 2);
        ctx.fillStyle = colors.fill;
        ctx.fill();

        if (isBloom) {
          ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
          ctx.lineWidth = 2.5;
          ctx.stroke();

          // Double ring effect for Bloom style
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, r + 4, 0, Math.PI * 2);
          ctx.strokeStyle = `${colors.fill}33`; // 20% opacity
          ctx.lineWidth = 1.5;
          ctx.stroke();
        } else {
          ctx.strokeStyle = colors.stroke;
          ctx.lineWidth = 2;
          ctx.stroke();
        }
        ctx.shadowBlur = 0;

        // Label
        if (isBloom) {
          const label = n.computedLabel || n.id;
          ctx.font = `bold 10px 'Inter', sans-serif`;
          const isDark = document.documentElement.classList.contains("dark");
          
          // Draw a small background capsule for text readability
          const textWidth = ctx.measureText(label).width;
          ctx.fillStyle = isDark ? "rgba(15, 23, 42, 0.75)" : "rgba(255, 255, 255, 0.8)";
          ctx.fillRect(pos.x - textWidth / 2 - 4, pos.y + r + 4, textWidth + 8, 14);
          
          ctx.fillStyle = isDark ? "#e2e8f0" : "#1e293b";
          ctx.textAlign = "center";
          ctx.textBaseline = "top";
          ctx.fillText(label, pos.x, pos.y + r + 6);
        } else {
          const maxLen = 12;
          const label = n.computedLabel?.length > maxLen ? n.computedLabel.slice(0, maxLen) + "…" : (n.computedLabel || n.id);
          ctx.font = `bold ${n.group === "schema" ? 8 : 9}px 'Inter', sans-serif`;
          ctx.fillStyle = colors.text;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(label, pos.x, pos.y);
        }
      });

      ctx.restore();
      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [selectedNode, graphMeta, visStyle]);

  // ── Mouse Events: Pan, Zoom, Click ───────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function toWorld(cx, cy) {
      const { scale, tx, ty } = transformRef.current;
      return { x: (cx - tx) / scale, y: (cy - ty) / scale };
    }

    function hitTest(wx, wy) {
      const { nodes } = graphRef.current;
      const physics = physicsRef.current;
      if (!physics) return null;
      for (const n of nodes) {
        const pos = physics.positions[n.id];
        if (!pos) continue;
        const r = n.group === "schema" ? 12 : 18;
        const dx = wx - pos.x, dy = wy - pos.y;
        if (dx * dx + dy * dy <= r * r) return n;
      }
      return null;
    }

    let isPanning = false;
    let lastPan = { x: 0, y: 0 };

    function onMouseDown(e) {
      const rect = canvas.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const world = toWorld(cx, cy);
      const hit = hitTest(world.x, world.y);

      if (hit) {
        setSelectedNode(hit);
        dragRef.current = { nodeId: hit.id, startX: cx, startY: cy };
      } else {
        isPanning = true;
        lastPan = { x: cx, y: cy };
        dragRef.current = null;
      }
    }

    function onMouseMove(e) {
      const rect = canvas.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;

      if (dragRef.current?.nodeId && physicsRef.current) {
        const world = toWorld(cx, cy);
        physicsRef.current.positions[dragRef.current.nodeId] = world;
        physicsRef.current.velocities[dragRef.current.nodeId] = { x: 0, y: 0 };
        physicsRef.current.ticks = 0; // restart physics
      } else if (isPanning) {
        transformRef.current.tx += cx - lastPan.x;
        transformRef.current.ty += cy - lastPan.y;
        lastPan = { x: cx, y: cy };
      }
    }

    function onMouseUp() {
      isPanning = false;
      dragRef.current = null;
    }

    function onWheel(e) {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const { scale, tx, ty } = transformRef.current;
      const newScale = Math.min(4, Math.max(0.2, scale * delta));
      transformRef.current.scale = newScale;
      transformRef.current.tx = cx - (cx - tx) * (newScale / scale);
      transformRef.current.ty = cy - (cy - ty) * (newScale / scale);
    }

    canvas.addEventListener("mousedown", onMouseDown);
    canvas.addEventListener("mousemove", onMouseMove);
    canvas.addEventListener("mouseup", onMouseUp);
    canvas.addEventListener("wheel", onWheel, { passive: false });

    return () => {
      canvas.removeEventListener("mousedown", onMouseDown);
      canvas.removeEventListener("mousemove", onMouseMove);
      canvas.removeEventListener("mouseup", onMouseUp);
      canvas.removeEventListener("wheel", onWheel);
    };
  }, []);

  // ── Zoom Helpers ─────────────────────────────────────────────────────────
  const zoom = (factor) => {
    const { scale, tx, ty } = transformRef.current;
    const cx = 400, cy = 300;
    const newScale = Math.min(4, Math.max(0.2, scale * factor));
    transformRef.current.scale = newScale;
    transformRef.current.tx = cx - (cx - tx) * (newScale / scale);
    transformRef.current.ty = cy - (cy - ty) * (newScale / scale);
  };

  const resetView = () => {
    transformRef.current = { scale: 1, tx: 0, ty: 0 };
    if (physicsRef.current) physicsRef.current.ticks = 0;
  };

  const typeColors = {
    domain: "bg-emerald-500",
    schema: "bg-indigo-500",
  };

  return (
    <div className={`flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-950 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <GitFork className="w-4 h-4 text-indigo-500" />
          <span className="text-sm font-bold text-slate-900 dark:text-white">Evidence Graph</span>
          {graphMeta.available !== null && (
            <Badge variant="outline" className="text-[9px] font-bold uppercase ml-1">
              {graphMeta.nodes} nodes · {graphMeta.edges} edges
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          {["all", "domain", "schema"].map(v => (
            <button
              key={v}
              onClick={() => setActiveView(v)}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase transition-all ${
                activeView === v
                  ? "bg-indigo-500 text-white shadow"
                  : "bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-400"
              }`}
            >
              {v}
            </button>
          ))}
          <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1" />
          
          {/* Style Toggle (Bloom model of visualization) */}
          <div className="flex bg-slate-100 dark:bg-slate-800 p-0.5 rounded-lg">
            {["standard", "bloom"].map(s => (
              <button
                key={s}
                onClick={() => setVisStyle(s)}
                className={`px-2.5 py-0.5 rounded-md text-[9px] font-extrabold uppercase transition-all ${
                  visStyle === s
                    ? "bg-white text-indigo-600 shadow-sm dark:bg-slate-700 dark:text-white"
                    : "text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1" />
          <button onClick={() => zoom(1.2)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"><ZoomIn className="w-3.5 h-3.5" /></button>
          <button onClick={() => zoom(0.8)} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"><ZoomOut className="w-3.5 h-3.5" /></button>
          <button onClick={resetView} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"><Maximize2 className="w-3.5 h-3.5" /></button>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-[10px]" onClick={() => fetchGraph(true)} disabled={loading}>
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {/* Canvas Area */}
      <div className="relative flex-1" style={{ minHeight: 380 }}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-slate-950/80 z-10">
            <div className="flex flex-col items-center gap-2 text-slate-400">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              <span className="text-xs">Building evidence graph…</span>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <div className="flex flex-col items-center gap-3 text-center max-w-xs px-6">
              <AlertTriangle className="w-8 h-8 text-amber-400" />
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Graph Unavailable</p>
              <p className="text-xs text-slate-400">{error}</p>
              <p className="text-[10px] text-slate-400">Run the audit evaluation first to populate the  entity graph.</p>
            </div>
          </div>
        )}

        {!loading && !error && graphMeta.nodes === 0 && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex flex-col items-center gap-3 text-center max-w-xs px-6">
              <GitFork className="w-8 h-8 text-slate-300" />
              <p className="text-sm font-semibold text-slate-500">No graph data yet</p>
              <p className="text-xs text-slate-400">Run the audit evaluation to generate entity relationships. The graph will populate automatically.</p>
            </div>
          </div>
        )}

        <canvas
          ref={canvasRef}
          className="w-full h-full cursor-grab active:cursor-grabbing"
          style={{ display: "block" }}
        />

        {/* Node Inspector */}
        {selectedNode && (
          <div className="absolute top-3 right-3 w-56 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/95 dark:bg-slate-900/95 shadow-xl p-4 space-y-2 backdrop-blur">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Node Inspector</span>
              <button onClick={() => setSelectedNode(null)} className="text-slate-400 hover:text-slate-600 text-xs">✕</button>
            </div>
            <p className="text-sm font-bold text-slate-900 dark:text-white truncate" title={selectedNode.computedLabel}>
              {selectedNode.computedLabel}
            </p>
            <Badge variant="outline" className="text-[9px]">{selectedNode.type}</Badge>
            <div className="space-y-1 pt-1 border-t border-slate-100 dark:border-slate-800">
              {selectedNode.properties && Object.entries(selectedNode.properties)
                .filter(([k, v]) => {
                  const kLower = k.toLowerCase();
                  const exactTechKeys = ["element_id", "run_id", "audit_run_id", "created_at", "updated_at", "metadata"];
                  
                  if (exactTechKeys.includes(kLower)) return false;
                  
                  const strVal = String(v);
                  if (strVal.includes(":ARTIFACT:") || strVal.length > 50) return false;
                  
                  if (kLower === "id" && (strVal.length > 20 || strVal.includes("-"))) {
                    return false;
                  }
                  
                  return true;
                })
                .slice(0, 10)
                .map(([k, v]) => (
                  <div key={k} className="flex justify-between text-[10px]">
                    <span className="text-slate-400 font-bold uppercase text-[8px]">{k.replace(/_/g, ' ')}</span>
                    <span className="text-slate-700 dark:text-slate-200 font-mono truncate max-w-[120px]" title={String(v)}>{String(v)}</span>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="px-5 py-2 border-t border-slate-100 dark:border-slate-800 text-[10px] text-slate-400">
        Drag nodes to reposition · Scroll to zoom · Click node to inspect
      </div>
    </div>
  );
}
